import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  useToast,
  Menu,
  MenuButton,
  MenuList,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiMoreVertical } from "react-icons/fi";
import { FaRegUser } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  deleteAVehicleSegment,
  fetchAllVehiclesList,
} from "../../../actions/management.action";
import { LogoutAlertDialog } from "../../../components/LogoutAlertDialog";
import { Loading } from "../../../components/Loading";
import useDeleteToast from "../../../components/DeleteToast";
import { DeleteAlert } from "../../../components/DeleteAlert";

const Vehicles = () => {
  const toast = useToast();
  const showDeleteToast = useDeleteToast();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    id: null,
  });
  const [loading, setLoading] = useState(true);
  const [vehicleSegments, setVehicleSegments] = useState([]);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const onCloseAlert = () => setIsAlertOpen(false);
  const onOpenAlert = () => setIsAlertOpen(true);

  const getVehiclesItem = async () => {
    try {
      const res = await fetchAllVehiclesList();
      setVehicleSegments(res?.data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteASegment = async (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteAVehicleSegment(deleteConfirm.id);
      showDeleteToast("Vehicle Segment deleted successfully");
      getVehiclesItem();
    } catch {
      toast({
        title: "Error",
        description: "Unable to delete Vehicle Segment",
        status: "error",
      });
    }
  };

  useEffect(() => {
    getVehiclesItem();
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      <DeleteAlert
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onDelete={onDeleteConfirm}
        label="Vehicle"
        itemName="Vehicle"
      />
      <Box px={4} py={6}>
        {/* Header */}
        <Flex
          justifyContent={{ base: "center", md: "space-between" }}
          alignItems={{ base: "flex-start", md: "center" }}
          mb={6}
          flexDir={{ base: "column", md: "row" }}
          gap={4}
        >
          <BreadCrumbMaker
            items={[
              { link: "/management", title: "Operations Setup" },
              { link: "/management/vehicles", title: "Vehicles", on: true },
            ]}
          />

          <Button
            as={Link}
            to="/management/vehicles/create"
            colorScheme="blue"
            size="sm"
            px={8}
            py={5}
            borderRadius="md"
          >
            Add Vehicle Segment
          </Button>
        </Flex>

        {/* Cards */}
        <Box bg="white" p={4} borderRadius="lg">
          <Grid gap={5}>
            {vehicleSegments.map((pkg) => (
              <Box
                key={pkg?._id}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={5}
                position="relative"
                transition="all 0.2s ease"
                cursor="pointer"
                onClick={() => navigate(`/management/vehicles/${pkg?._id}`)}
                _hover={{
                  boxShadow: "lg",
                  borderColor: "blue.300",
                  transform: "translateY(-2px)",
                }}
              >
                {/* Clickable Area */}
                <Flex gap={4} flexDir={{ base: "column", md: "row" }}>
                  {/* Image */}
                  <Image
                    src={pkg?.images?.[0] || "/images.png"}
                    alt={pkg?.segmentName}
                    w="160px"
                    h="110px"
                    rounded="lg"
                    objectFit="cover"
                    border="1px solid"
                    borderColor="gray.200"
                    fallbackSrc="/images.png"
                  />

                  {/* Content */}
                  <VStack align="flex-start" spacing={2} flex={1}>
                    <Heading fontSize="lg" fontWeight="600" color="gray.800">
                      {pkg?.segmentName}
                    </Heading>

                    <Flex gap={3} flexWrap="wrap" align="center">
                      <Badge colorScheme="orange">
                        {pkg?.vehicleTypes || "Economy"}
                      </Badge>

                      <Flex align="center" gap={1} color="gray.600">
                        <FaRegUser />
                        <Text fontSize="sm">
                          {pkg?.passengerCapacity} Seats
                        </Text>
                      </Flex>

                      <Badge
                        colorScheme={pkg?.status === "active" ? "green" : "red"}
                        textTransform="capitalize"
                      >
                        {pkg?.status || "N/A"}
                      </Badge>
                    </Flex>
                  </VStack>
                </Flex>

                {/* Menu */}
                <Box position="absolute" top={3} right={3}>
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      size="sm"
                      rounded="full"
                      _hover={{ bg: "gray.100" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FiMoreVertical />
                    </MenuButton>
                    <MenuList p={2} boxShadow="lg">
                      <Link
                        to={`/management/vehicles/edit/${pkg?._id}`}
                        style={{ textDecoration: "none" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          p={2}
                          _hover={{ bg: "gray.100", color: "blue.500" }}
                        >
                          Edit
                        </Box>
                      </Link>

                      <Box
                        p={2}
                        _hover={{ bg: "gray.100", color: "red.500" }}
                        cursor="pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteASegment(pkg?._id);
                        }}
                      >
                        Delete
                      </Box>
                    </MenuList>
                  </Menu>
                </Box>

                {/* Details */}
                <Flex flexWrap="wrap" gap={6} mt={5}>
                  {/* Features */}
                  {pkg?.amenities?.length > 0 && (
                    <Box minW="200px">
                      <Text fontWeight="600" fontSize="sm" mb={2}>
                        Features
                      </Text>
                      {pkg.amenities
                        .filter((f) => f?.trim())
                        .map((feat, i) => (
                          <Text key={i} fontSize="sm" color="gray.600">
                            • {feat}
                          </Text>
                        ))}
                    </Box>
                  )}

                  {/* Fuels */}
                  {pkg?.fuelTypes?.length > 0 && (
                    <Box minW="200px">
                      <Text fontWeight="600" fontSize="sm" mb={2}>
                        Fuel Types
                      </Text>
                      {pkg.fuelTypes
                        .filter((f) => f?.trim())
                        .map((fuel, i) => (
                          <Text key={i} fontSize="sm" color="gray.600">
                            • {fuel}
                          </Text>
                        ))}
                    </Box>
                  )}

                  {/* Inclusions */}
                  {pkg?.inclusions?.length > 0 && (
                    <Box minW="200px">
                      <Text
                        fontWeight="600"
                        fontSize="sm"
                        mb={2}
                        color="green.600"
                      >
                        ✓ Inclusions
                      </Text>
                      {pkg.inclusions
                        .filter((i) => i.checked)
                        .map((item, i) => (
                          <Text key={i} fontSize="sm" color="gray.600">
                            • {item.label}
                          </Text>
                        ))}
                    </Box>
                  )}

                  {/* Exclusions */}
                  {pkg?.exclusions?.length > 0 && (
                    <Box minW="200px">
                      <Text
                        fontWeight="600"
                        fontSize="sm"
                        mb={2}
                        color="red.500"
                      >
                        ✗ Exclusions
                      </Text>
                      {pkg.exclusions
                        .filter((i) => i.checked)
                        .map((item, i) => (
                          <Text key={i} fontSize="sm" color="gray.600">
                            • {item.label}
                          </Text>
                        ))}
                    </Box>
                  )}
                </Flex>
              </Box>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Vehicles;
